import { Link } from '@tanstack/react-router';

import {
  DisplayHeading,
  Eyebrow,
  IndexNumber,
  Tag,
} from '@/components/editorial';
import { Films } from '@/components/films';
import { filmsData } from '@/lib/data';
import {
  dailyDriverAppsData,
  favoriteStackData,
  snippetsData,
} from '@/lib/personal';

const sectionLinks = [
  { href: '#apps', label: 'Apps' },
  { href: '#stack', label: 'Stack' },
  { href: '#snippets', label: 'Snippets' },
  { href: '#films', label: 'Films' },
];

export function PersonalStuffPageContent() {
  return (
    <div className="container max-w-5xl px-6 py-16 md:py-24">
      <div className="mb-10">
        <Eyebrow className="mb-4">Off the clock</Eyebrow>
        <DisplayHeading as="h1" size="xl" animate={false}>
          Personal stuff
        </DisplayHeading>
        <p className="text-muted-foreground mt-4 max-w-2xl text-lg leading-relaxed">
          Films, books, tools I actually reach for, small snippets, channels and
          blogs I follow, plus the devices I use (affiliate links where noted).
          Dummy seeds included; I&apos;ll swap in the real picks over time.
        </p>
      </div>

      <nav
        aria-label="Personal stuff sections"
        className="border-border mb-16 flex flex-wrap gap-2 border-y py-4"
      >
        {sectionLinks.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className="text-muted-foreground hover:text-brand font-mono text-xs uppercase tracking-widest transition-colors"
          >
            {item.label}
          </a>
        ))}
      </nav>

      <section id="apps" className="scroll-mt-28">
        <Eyebrow className="mb-4">Toolkit</Eyebrow>
        <DisplayHeading as="h2" size="lg" animate={false}>
          Daily driver apps
        </DisplayHeading>
        <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed">
          The tools I keep open every day.
        </p>
        <div className="mt-8 space-y-8">
          {(['IDE', 'LLM', 'AI Tools', 'Git', 'Deployment'] as const).map(
            (category) => (
              <div key={category}>
                <h3 className="text-muted-foreground mb-4 font-mono text-xs font-semibold uppercase tracking-widest">
                  {category}
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                  {dailyDriverAppsData
                    .filter((app) => app.category === category)
                    .map((app) => (
                      <a
                        key={app.name}
                        href={app.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border-border group flex items-center justify-between rounded-xl border p-4 transition-colors hover:border-brand"
                      >
                        <div>
                          <p className="font-display text-ink group-hover:text-brand text-base font-semibold transition-colors">
                            {app.name}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {app.category}
                          </p>
                        </div>
                        <span className="text-brand shrink-0 font-mono text-xs">
                          ↗
                        </span>
                      </a>
                    ))}
                </div>
              </div>
            )
          )}
        </div>
      </section>

      <hr className="border-border my-16" />

      <section id="stack" className="scroll-mt-28">
        <Eyebrow className="mb-4">Daily drivers</Eyebrow>
        <DisplayHeading as="h2" size="lg" animate={false}>
          Favorite tech stack
        </DisplayHeading>
        <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed">
          Not everything I&apos;ve touched, just the tools I prefer when I get
          to choose.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {favoriteStackData.map((item, index) => (
            <div
              key={item.name}
              className="border-border flex flex-col gap-2 rounded-xl border p-5"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display text-ink text-base font-semibold">
                  {item.name}
                </h3>
                <IndexNumber value={index + 1} />
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {item.note}
              </p>
            </div>
          ))}
        </div>
      </section>

      <hr className="border-border my-16" />

      <section id="snippets" className="scroll-mt-28">
        <Eyebrow className="mb-4">Pocket notes</Eyebrow>
        <DisplayHeading as="h2" size="lg" animate={false}>
          Snippets
        </DisplayHeading>
        <p className="text-muted-foreground mt-3 max-w-xl text-sm leading-relaxed">
          Short tips and tiny code bits. Seeded with placeholders for now; more
          coming.
        </p>
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {snippetsData.map((snippet) => (
            <article
              key={snippet.id}
              className="border-border flex flex-col gap-3 rounded-xl border p-5"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-display text-ink text-base font-semibold">
                  {snippet.title}
                </h3>
                <Tag variant="muted">
                  {snippet.kind === 'code'
                    ? (snippet.language ?? 'code')
                    : 'tip'}
                </Tag>
              </div>
              {snippet.kind === 'code' ? (
                <pre className="bg-muted overflow-x-auto rounded-lg p-4 font-mono text-xs leading-relaxed">
                  <code>{snippet.body}</code>
                </pre>
              ) : (
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {snippet.body}
                </p>
              )}
            </article>
          ))}
        </div>
      </section>

      <hr className="border-border my-16" />

      <section id="films" className="scroll-mt-28">
        <Films embedded />
        <p className="text-muted-foreground mt-4 font-mono text-xs">
          {filmsData.length} films · links open on Letterboxd
        </p>
      </section>

      <div className="border-border bg-muted/30 mt-16 rounded-2xl border p-6 md:p-8">
        <p className="font-display text-ink text-xl font-semibold">
          Want the professional side?
        </p>
        <p className="text-muted-foreground mt-2 max-w-lg text-sm leading-relaxed">
          Experience, services, and selected work live on the rest of the site.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/about"
            className="bg-brand text-primary-foreground hover:bg-brand-soft rounded-full px-5 py-2 font-mono text-xs font-medium transition-colors"
          >
            About me
          </Link>
          <Link
            to="/services"
            className="border-border text-ink hover:border-brand hover:text-brand rounded-full border px-5 py-2 font-mono text-xs font-medium transition-colors"
          >
            Services
          </Link>
          <Link
            to="/contact"
            className="border-border text-ink hover:border-brand hover:text-brand rounded-full border px-5 py-2 font-mono text-xs font-medium transition-colors"
          >
            Contact
          </Link>
        </div>
      </div>

      <div className="mt-12">
        <Link
          to="/"
          className="text-muted-foreground hover:text-brand font-mono text-sm transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
